import { getRequestWithAuth, postRequestWithAuth } from "../api";

type BaseRequestBody = {
  connection_type: string;
  attachments: any[];
  name: string;
};

type ShopifyRequestBody = BaseRequestBody & {
  details: {
    shop_name: string;
    webhook_version: string;
    access_token: string;
  };
};

type InstagramRequestBody = BaseRequestBody & {
  details: {
    instagram_business_account_id?: string;
    facebook_page_id?: string;
    access_token: string;
    token_expiry: string;
    ad_account_ids?: string[];
  };
};

type PostgreSQLRequestBody = BaseRequestBody & {
  details: {
    host: string;
    port: string;
    username: string;
    password: string;
    database: string;
  };
};

export const addDatabaseConnection = async (data: any) => {
  let requestBody:
    | ShopifyRequestBody
    | InstagramRequestBody
    | PostgreSQLRequestBody;

  if (data.type === "shopify") {
    requestBody = {
      connection_type: data.type,
      details: {
        shop_name: data.shop_name,
        webhook_version: data.webhook_version,
        access_token: data.access_token,
      },
      name: data.connection_name || "Shopify Store",
      attachments: data.attachments || [],
    };
  } else if (data.type === "instagram") {
    requestBody = {
      connection_type: data.type,
      details: {
        instagram_business_account_id: data.instagram_business_account_id,
        facebook_page_id: data.facebook_page_id,
        access_token: data.access_token,
        token_expiry: data.token_expiry,
        ad_account_ids: data.ad_account_ids,
      },
      name: data.connection_name || "Instagram Business Account",
      attachments: data.attachments || [],
    };
  } else if (data.type === "postgresql") {
    requestBody = {
      connection_type: data.type,
      details: {
        host: data.host,
        port: data.port,
        username: data.username,
        password: data.password,
        database: data.database,
      },
      name: data.connectionName || data.database,
      attachments: data.attachments || [],
    };
  } else {
    return Promise.reject(new Error("Invalid connection type"));
  }
  const response = await postRequestWithAuth(
    "/api/v1/connections/addDataConnection",
    requestBody,
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );

  return response;
};

export const updateDatabaseConnection = async (
  connectionId: string,
  data: any
) => {
  let details: any;

  if (data.type === "shopify") {
    details = {
      shop_name: data.shop_name,
      webhook_version: data.webhook_version,
      access_token: data.access_token,
    };
  } else if (data.type === "instagram") {
    details = {
      instagram_business_account_id: data.instagram_business_account_id,
      facebook_page_id: data.facebook_page_id,
      access_token: data.access_token,
      token_expiry: data.token_expiry,
      ad_account_ids: data.ad_account_ids,
    };
  } else {
    // PostgreSQL (default)
    details = {
      host: data.host,
      port: data.port,
      username: data.username,
      password: data.password,
      database: data.database,
    };
  }

  const requestBody = {
    connection_id: connectionId,
    connection_type: data.type,
    details,
    name: data.connectionName || data.connection_name,
    attachments: data.attachments || [],
  };
  const response = await postRequestWithAuth(
    "/api/v1/connections/updateDataConnection",
    requestBody,
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );

  return response;
};

export const getDatabaseConnections = async () => {
  const response = await getRequestWithAuth(
    "/api/v1/connections/listDataConnections"
  );
  return response;
};

export const getDatabaseConnectionDetails = async (connectionId: string) => {
  const response = await getRequestWithAuth(
    `/api/v1/connections/fetchConnectionDetails?connection_id=${connectionId}`
  );
  return response;
};

export const deleteDatabaseConnection = async (connectionId: string) => {
  const response = await postRequestWithAuth(
    `/api/v1/connections/deleteDataConnection`,
    { connection_id: connectionId },
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
  return response;
};

export const uploadDataConnectionEnrichment = async (
  fileId: string,
  attachment: File
) => {
  const formData = new FormData();
  formData.append("file_id", fileId);
  formData.append("attachment", attachment);
  const response = await postRequestWithAuth(
    `/api/v1/dataEnrichment/uploadEnrichment`,
    formData,
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return response;
};

export const deleteDataConnectionEnrichment = async (
  files: {
    file_id: string;
    file_name: string;
  }[],
  connectionId?: string
) => {
  const requestBody = {
    files,
    connection_id: connectionId || null,
  };

  const response = await postRequestWithAuth(
    "/api/v1/dataEnrichment/deleteEnrichment",
    requestBody,
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );

  return response;
};

// Instagram ad account fetching
type AdAccount = {
  id: string;
  name: string;
  status?: number;
};

type InstagramAdAccountsResponse = {
  ad_accounts: AdAccount[];
};

export const fetchInstagramAdAccounts = async (
  accessToken: string
): Promise<InstagramAdAccountsResponse> => {
  const response = await postRequestWithAuth(
    "/api/v1/connections/instagram/fetch-ad-accounts",
    { access_token: accessToken },
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );

  return response as InstagramAdAccountsResponse;
};
